import type { GuessProblem } from '../../../../shared/code-breaker';
import type { codeBreaker as en } from '../en/code-breaker';

const problems: Readonly<Record<GuessProblem, string>> = {
  tooShort: '请输入四个数字',
  repeatedDigit: '四个数字不能重复',
  alreadyGuessed: '已经猜过了',
};

export const codeBreaker: typeof en = {
  problem: (problem) => problems[problem],
  guessFailed: '这次猜测没能送达房间，请再试一次。',
  crackedChat: '你猜中了。要等答案揭晓才能聊天。',
  chatPlaceholder: '说点什么…',
  codeOf: (round, rounds) => `第 ${round} / ${rounds} 题`,
  gameOver: '游戏结束',
  gameEnded: '游戏已中止',
  waitingRoom: '等待室',
  codesDetail: (codes) => `共 ${codes} 题。`,
  codesCracked: (codes) => `猜中 ${codes} 题`,
  alone: '叫上好友更好玩。',
  setup: (players, codes) =>
    `${players} 人，${codes} 题。所有人同时猜同一个四位数，次数越少得分越高。`,
  guestSetup: (players, codes) => `${players} 人，${codes} 题。`,
  crackedWatch: '你猜中了。等其他人猜完，一起揭晓。',
  outWatch: '次数用完了。揭晓时会公布答案。',
  others: '其他玩家',
  everyone: '所有玩家',
  everyoneGuessing: '大家正在猜数字',
  nobodyCracked: '没有人猜中',
  pegsOnly: '只看标记，不显示数字',
  crackedPoints: (points) => `猜中 · +${points}`,
  outOfGuesses: '次数用完了',
  noGuesses: '还没猜过',
  guessesCount: (count) => `猜了 ${count} 次`,
  codeResults: (round) => `第 ${round} 题的结果`,
  codeWas: (code) => `答案是 ${code}。`,
  crackedInPoints: (guesses, points) => `${guesses} 次猜中 · +${points}`,
  missed: '未猜中',
  paused: '已暂停',
  codeWasLabel: '答案是',
  youCracked: (guesses, points) => `你用 ${guesses} 次猜中，获得 +${points} 分`,
  youMissed: '这次没有猜中',
  nextCode: '下一题',
  finalScores: '最终得分',
  gotIt: (guesses, points) => `${guesses} 次猜中！+${points}`,
  waitingCount: (count) => `正在等 ${count} 位玩家`,
  everyoneDone: '所有人都猜完了',
  toGuess: '猜数字时间',
  crackCode: '猜出答案',
  crackedCount: (count) => `${count} 人猜中了`,
  fewerScore: '次数越少，得分越高',
  away: '暂离',
  waiting: '等待中',
  crackedItPoints: (points) => `猜中 · +${points}`,
  guessing: '猜数字中',
  yourGuesses: '你的猜测',
  row: (guess, mark) => `${[...guess].join(' ')}：${mark}`,
  legend: {
    right: '数字和位置都正确',
    moved: '数字在答案中，但位置不对',
    none: '不在答案中',
    count: '标记只数个数，不说是哪几个数字。',
  },
  legendLine: '绿色：位置正确。珊瑚色：位置不对。',
  freshPrompt: '输入四个不同的数字，按回车确认。',
  nextPrompt: '回车检查这一行，退格删除数字。',
  notesPrompt: '笔记已打开：按数字键标为不在、再按标为在、第三次清除。',
  codeNumber: (puzzle) => `第 ${puzzle} 题`,
  today: '今日题目',
  dailyFresh: '十次机会，每天一道新题。',
  readIt: '怎么看标记',
  shareDaily: (puzzle) => `猜数字 #${puzzle}`,
  crackedIn: (guesses) => `${guesses} 次猜中。`,
  notThisTime: '这次没有猜中。',
  dailyCracked: (code, streak) => `答案是 ${code}。${streak}`,
  dailyMissed: '明天重新开始连胜。',
  guesses: '猜测次数',
  guessesOf: (count, max) => `${count} / ${max}`,
  nextCodeIn: '距下一题',
  share: '分享',
  practiceCode: '练习一题',
  guessesToCrack: '猜中次数分布',
  streak: (days) => (days > 1 ? `已连续猜中 ${days} 天。` : '连胜开始了。'),
  newCodeAtMidnight: '午夜换新题。',
  youCrackedCode: (code) => `你破解了 ${code}`,
  challenge: '挑战',
  practice: '练习',
  friendCode: '好友的题',
  challengeFresh: '好友发来了这道题，有十次机会。',
  practiceFresh: '十次机会，练习不计入统计。',
  sharePractice: '猜数字练习',
  practiceResult: (code) => `答案是 ${code}。练习不计入统计。`,
  practiceMissed: '练习不计入统计。',
  anotherCode: '再来一题',
  challengeFriend: '挑战好友',
  todayCode: '今日一题',
  friendFewer: (guesses) =>
    `好友会猜同一道题，试着用少于 ${guesses} 次猜中。他们只能看到你的标记，看不到数字。`,
  friendSame: '好友会猜同一道题。他们只能看到你的标记，看不到数字。',
  yourPegs: '你的标记',
  onDevice: '本机记录',
  stats: {
    played: '已玩',
    cracked: '猜中率',
    currentStreak: '当前连胜',
    bestStreak: '最长连胜',
  },
  copied: '已复制',
  guessNumber: (guess, max) => `第 ${guess} / ${max} 次`,
  fixRow: '修改这一行，再试一次',
  known: (guesses, parts) =>
    `已猜 ${guesses} 次。${parts.length ? parts.join('') : '还没有确定的数字。'}`,
  digitsIn: (digits) => `${digits} 在答案中。`,
  digitsOut: (digits) => `${digits} 不在答案中。`,
  distribution: (count, guesses) => `${count} 题用 ${guesses} 次猜中`,
};
